/**
 * Cool workspace product catalog (Mary's Product_Landing_Cool_Mockup).
 *
 * Product enrollment gating is deferred. If a product has a route, open it.
 */

export const WORKSPACE_PRODUCTS = [
  {
    key: 'coverage',
    name: 'Keep Me Covered',
    chrome: 'Coverage Risk',
    blurb: 'Coverage risk, action queue, and eligibility — who is about to lose care.',
    route: '/coverage-risk',
    featureAny: ['coverage', 'keep_me_covered', 'kmc_select'],
    ctaOpen: 'Open product',
    ctaLearn: 'See 30-second verification',
  },
  {
    key: 'document',
    nameHtml: 'Document <span class="iq">IQ</span>',
    chrome: "My Doc IQ's",
    blurb: 'Extract clinical documents, build auth packs, update policies, and run pre-admission intake.',
    route: '/aiq-ai',
    featureAny: ['adi', 'aiq_intel'],
    ctaOpen: 'Open product',
    ctaLearn: 'See document workflows',
  },
  {
    key: 'policy',
    nameHtml: 'Policy <span class="iq">IQ</span>',
    chrome: 'IQ Hub · This Week',
    blurb: 'Weekly brief and landscape — what is changing and what to do.',
    route: '/iq-hub',
    featureAny: ['policy_engine'],
    ctaOpen: 'Open product',
    ctaLearn: 'See policy intelligence',
  },
  {
    key: 'community',
    nameHtml: 'Community <span class="iq">IQ</span>',
    chrome: 'Resource Navigator',
    blurb: 'Resource navigator — local supports and referral pathways.',
    route: '/resource-navigator',
    featureAny: ['community_iq'],
    ctaOpen: 'Open product',
    ctaLearn: 'See community resources',
  },
  {
    key: 'advocacy',
    nameHtml: 'Advocacy <span class="iq">IQ</span>',
    chrome: 'Advocacy Home',
    blurb: 'Monitor bills and proposals. Manage association actions. Mobilize member campaigns.',
    route: '/advocacy-iq',
    featureAny: ['advocacy_iq'],
    ctaOpen: 'Open product',
    ctaLearn: 'Learn about this product',
  },
];

export const LEARN_COPY = {
  coverage: {
    label: 'KEEP ME COVERED',
    headline: 'Verify coverage in 30 seconds. Stop funding bad intakes.',
    desc: 'One lookup returns five domains of data — eligibility, claims, discovery, life events, and network. What used to take a staff member 20 minutes now takes seconds.',
    statsClass: 'cols-2x2',
    stats: [
      { num: '30s', blue: true, txt: 'to verify coverage — confirmed before a staff member finishes the intake question' },
      { num: '$0', blue: false, txt: 'wasted on bad payers — network mismatch flagged before enrollment, not after a claim is denied' },
      { num: '5', blue: true, txt: 'domains checked at once: eligibility, claims, discovery, life events, and network' },
      { num: '100%', blue: false, txt: 'of enrolled leads monitored post-enrollment — alerts on any coverage change' },
    ],
    features: [
      { icon: 'fa-list-check', title: 'Batch/bulk runs', body: 'send a full client list and get eligibility checked across all members at once' },
      { icon: 'fa-bolt', title: 'Real-time lookup', body: 'instant single-member check at the point of intake, embedded in Salesforce' },
    ],
  },
  document: {
    label: 'DOCUMENT IQ',
    headline: 'Turn every document into a decision — before the audit.',
    desc: 'Extract clinical documents, run auth gap analysis, build authorization packs, update policies and procedures, and manage pre-admission intake — one queue for your team.',
    statsClass: 'cols-2x2',
    stats: [
      { num: 'Extract', blue: true, txt: 'plans of care, physician orders, and clinical notes structured and searchable' },
      { num: 'Auth pack', blue: false, txt: 'cross-document gap review with authorization letter drafts when gaps close' },
      { num: 'Policy', blue: true, txt: 'build or remediate home health SOP templates against regulatory requirements' },
      { num: 'Intake', blue: false, txt: 'pre-admissions synthesis and intake workflow in one operational queue' },
    ],
    features: [
      { icon: 'fa-notes-medical', title: 'Extract', body: 'plans of care, physician orders, and clinical notes pulled into structured review' },
      { icon: 'fa-file-signature', title: 'Auth Analysis & Pack', body: 'gap checklist across patient documents plus authorization letter pack' },
      { icon: 'fa-book', title: 'Policy & Procedures', body: 'build or update SOP templates from deficiency letters or scratch' },
      { icon: 'fa-user-plus', title: 'Pre-Admissions / Intake', body: 'multi-document admission analysis and intake decision workflow' },
    ],
  },
  policy: {
    label: 'POLICY IQ',
    headline: 'Know what every policy change means — before it hits.',
    desc: 'PolicyIQ reads 17,000+ CMS updates per year. Your staff sees only what’s relevant to them, personalized per user, with plain-language briefings on what to do next.',
    statsClass: 'cols-2x2',
    stats: [
      { num: '$5,500', blue: false, txt: 'max CMS daily fine — a 90-day gap costs $495,000 in penalties before remediation' },
      { num: '60–90d', blue: true, txt: 'ahead of enactment — signals surface while pending, when advocacy is still winnable' },
      { num: '$20–40K', blue: false, txt: 'per market study replaced — Landscape delivers live county-level intelligence, always current' },
      { num: '10–15%', blue: true, txt: 'of annual Medicaid revenue at risk from a single policy change' },
    ],
    features: [
      { icon: 'fa-compass', title: 'IQ Hub', body: 'legislation and federal/state signals surfaced the moment they’re introduced' },
      { icon: 'fa-globe', title: 'Landscape', body: 'live county-level market intelligence across all your states, on demand' },
      { icon: 'fa-robot', title: 'Pollie AI', body: 'any policy question answered instantly, sourced from your corpus and live signals' },
    ],
  },
  community: {
    label: 'COMMUNITY IQ',
    headline: 'Help every patient find care — wherever they are.',
    desc: 'A living resource network that connects patients and care coordinators to the right local services, whether they’re in a rural county with limited options or an urban center that’s hard to navigate.',
    statsClass: 'cols-2',
    stats: [
      { num: 'Local', blue: false, txt: 'resources mapped by geography — rural, suburban, and urban coverage' },
      { num: 'Current', blue: true, txt: 'directory actively maintained so your team never sends a patient to a dead end' },
    ],
    features: [
      { icon: 'fa-map-marker-alt', title: '', body: 'Geographically-aware resource directory spanning housing, food, transportation, behavioral health, and more' },
      { icon: 'fa-user-friends', title: '', body: 'Helps care coordinators find community supports fast — fewer calls, fewer dead ends for patients' },
      { icon: 'fa-home', title: '', body: 'Covers rural access gaps with targeted resources often missing from generic national databases' },
      { icon: 'fa-sync-alt', title: '', body: 'Actively maintained so your team isn’t working from stale or disconnected listings' },
      { icon: 'fa-heart', title: '', body: 'Built around the full continuum of care — clinical and the social' },
    ],
  },
  advocacy: {
    label: 'ADVOCACY IQ',
    headline: 'Monitor. Manage. Mobilize.',
    desc: 'Monitor bills and proposals — movement, hearings, and decisions. Manage bill movement and association activities with Advocacy Activity Manager. Mobilize members when it\'s time to act.',
    statsClass: 'cols-3',
    stats: [
      { num: 'Monitor', blue: true, txt: 'bills and proposals — movement, hearings, and decisions; alerts when something changes' },
      { num: 'Manage', blue: false, txt: 'bill movement and association activities; Advocacy Activity Manager shows members a live view on your website' },
      { num: 'Mobilize', blue: true, txt: 'campaigns to members — legislators matched to every service ZIP in one action' },
    ],
    features: [
      { icon: 'fa-eye', title: 'Legislative Watch', body: 'monitor bills and proposals; alerts on movement, hearings, and decisions' },
      { icon: 'fa-clipboard-list', title: 'Advocacy Activity Manager', body: 'manage bill movement and association activities; live member view on your website' },
      { icon: 'fa-bullhorn', title: 'Advocacy Campaigns', body: 'from any bill or your own topic; suggested campaigns for staff to consider' },
      { icon: 'fa-map-marker-alt', title: 'Multi-ZIP geography', body: 'pre-filled service ZIPs, auto-selected legislators, one action for every ZIP' },
      { icon: 'fa-envelope', title: 'Member email', body: 'personalized links with name, company, and action pre-filled' },
    ],
  },
};

/**
 * @param {{ features_enabled?: Record<string, boolean> } | null | undefined} org
 * @param {{ featureAny: string[] }} product
 * @returns {boolean}
 */
export function isWorkspaceProductOpen(org, product) {
  const flags = org?.features_enabled || {};
  if (!product?.featureAny?.length) return false;
  if (flags.all_features) {
    return product.featureAny.some((key) => [
      'coverage', 'keep_me_covered', 'kmc_select', 'adi', 'aiq_intel', 'policy_engine',
      'advocacy_iq', 'community_iq',
    ].includes(key));
  }
  return product.featureAny.some((key) => !!flags[key]);
}

/**
 * Whether a click should navigate (vs Learn more).
 *
 * @param {{ features_enabled?: Record<string, boolean> } | null | undefined} org
 * @param {{ route?: string | null, featureAny: string[] }} product
 * @param {{ platformStaff?: boolean }} [opts]
 * @returns {boolean}
 */
export function isWorkspaceProductNavigable(org, product, opts = {}) {
  if (!product?.route) return false;
  if (opts.platformStaff) return true;
  return isWorkspaceProductOpen(org, product);
}
